import Breadcrumb from "@/components/Breadcrumb";
import PageHeading from "@/components/PageHeading/PageHeading";
import Tabs from "@/components/Tabs";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import { useParams } from "@/hooks/useSearchParams";
import AppLayout from "@/layouts/AppLayout";
import type { Path } from "@/router";
import { useMembershipServicesNavLinks } from "../../../_membershipServicesNavLinks";
import { MEMBER_ELECTIONS_PAST_PATH, MEMBER_ELECTIONS_PENDING_PATH } from "../constants";
import type { MemberElectionsTab } from "../types";
import PastMemberElectionsContent from "./PastMemberElectionsContent";
import PendingMemberElectionsContent from "./PendingMemberElectionsContent";

const MEMBER_ELECTION_TABS: { label: string; link: Path; tab: MemberElectionsTab }[] = [
  { label: "Pending Electees", link: MEMBER_ELECTIONS_PENDING_PATH, tab: "pending" },
  { label: "Past Elections", link: MEMBER_ELECTIONS_PAST_PATH, tab: "past" }
];

function memberElectionTabLink(link: Path, searchParamsUrl: string) {
  const searchParams = new URLSearchParams(searchParamsUrl);
  searchParams.delete("after");
  searchParams.delete("before");
  searchParams.delete("currentPage");

  const search = searchParams.toString();
  return `${link}${search ? `?${search}` : ""}` as Path;
}

export function MemberElectionsPageContent({ activeTab }: { activeTab: MemberElectionsTab }) {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  usePageTitle(`/people/membership-services/member-elections/${activeTab}`, "People | Member Elections");
  const { searchParamsUrl } = useParams();
  const navLinks = useMembershipServicesNavLinks();

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb
        title="Operations"
        compactTitle="Member Election Management"
        backLink={{ to: "/people/membership-services" }}
      />
      <PageHeading title="Membership Elections" compact />
      <Tabs
        tabItems={MEMBER_ELECTION_TABS.map((tab) => ({
          label: tab.label,
          link: memberElectionTabLink(tab.link, searchParamsUrl),
          active: tab.tab === activeTab
        }))}
      />
      <div className="w-full">
        {activeTab === "pending" ? <PendingMemberElectionsContent /> : <PastMemberElectionsContent />}
      </div>
    </AppLayout>
  );
}
